import React from 'react';
import { Icon } from '@iconify/react';
import { PageCard, PageField } from '../../../components/PrivatePage';
import { AppButton } from '../../../components/AppButton/AppButton';
import SelectSymbol from '../../../components/SelectSymbol/SelectSymbol';
import SwitchInput from '../../../components/SwitchInput/SwitchInput';
import ConditionsArea from '../AutomationModal/ConditionsArea/ConditionsArea';
import ActionsArea from '../AutomationModal/ActionsArea/ActionsArea';
import ScheduleArea from '../AutomationModal/ScheduleArea/ScheduleArea';

import LogButton from '../../../components/Logs/LogButton';
import LogView from '../../../components/Logs/LogView';
import '../../../components/PrivatePage/private-page.css';
import './scheduled-bot.css';

/**
 * props:
 * - automation
 * - indexes
 * - showLogs
 * - error
 * - onInputChange
 * - onLogClick
 * - onSubmit
 */
function ScheduledBotForm({
    automation,
    indexes,
    showLogs,
    error,
    onInputChange,
    onLogClick,
    onSubmit,
}) {
    if (showLogs) {
        return (
            <div className="bot-setup-form">
                <PageCard icon="mdi:text-box-outline" title="Logs" description="Automation execution history.">
                    <LogView file={`A:${automation.id}`} />
                </PageCard>
            </div>
        );
    }

    return (
        <div className="bot-setup-form">
            <PageCard
                icon="mdi:tune-variant"
                title="Bot setup"
                description="Choose the trading pair and name for your scheduled bot."
            >
                <PageField label="Trading pair">
                    <SelectSymbol
                        onChange={onInputChange}
                        symbol={automation.symbol}
                        disabled={automation.id > 0}
                        fullWidth
                    />
                </PageField>
                <PageField label="Name" htmlFor="name">
                    <input
                        className="form-control"
                        id="name"
                        type="text"
                        placeholder="My scheduled bot name"
                        value={automation.name || ''}
                        required
                        onChange={onInputChange}
                    />
                </PageField>
            </PageCard>

            <PageCard
                icon="mdi:clock-outline"
                title="Schedule"
                description="Run on a specific date and time, or use a CRON expression for recurring runs."
            >
                <ScheduleArea
                    schedule={automation.schedule}
                    onChange={onInputChange}
                    variant="stacked"
                />
            </PageCard>

            <PageCard
                icon="mdi:source-branch"
                title="Conditions & actions"
                description={
                    automation.symbol
                        ? 'Set optional filters and actions to run when the schedule fires.'
                        : 'Select a trading pair in Bot setup before adding conditions and actions.'
                }
            >
                <PageField label="Conditions">
                    <ConditionsArea
                        symbol={automation.symbol}
                        conditions={automation.conditions}
                        indexes={indexes}
                        onChange={onInputChange}
                    />
                </PageField>
                <PageField label="Actions">
                    <ActionsArea
                        symbol={automation.symbol}
                        actions={automation.actions}
                        onChange={onInputChange}
                    />
                </PageField>
            </PageCard>

            <PageCard
                icon="mdi:cog-outline"
                title="Options"
                description="Position limits and runtime settings."
            >
                <PageField label="Max open positions" htmlFor="maxOpenPositions">
                    <input
                        id="maxOpenPositions"
                        type="number"
                        min="0"
                        step="1"
                        className="form-control"
                        value={automation.maxOpenPositions ?? 0}
                        onChange={onInputChange}
                    />
                    <p className="private-page-field-hint">
                        0 = unlimited. Counts filled open positions (buys minus sells), not pending orders.
                    </p>
                </PageField>
                <div className="d-flex flex-wrap gap-4 mt-2">
                    <SwitchInput
                        id="isActive"
                        text="Is active?"
                        onChange={onInputChange}
                        isChecked={automation.isActive}
                    />
                    <SwitchInput
                        id="logs"
                        text="Enable logs?"
                        onChange={onInputChange}
                        isChecked={automation.logs}
                    />
                </div>
            </PageCard>

            {error ? (
                <div className="private-page-notice private-page-notice--error" role="alert">
                    <Icon icon="mdi:alert-circle-outline" aria-hidden="true" />
                    <span>{error}</span>
                </div>
            ) : null}

            <div className="private-page-actions" style={{ justifyContent: 'flex-start' }}>
                <LogButton id={automation.id} onClick={onLogClick} />
                <AppButton
                    variant="primary"
                    size="md"
                    onClick={onSubmit}
                >
                    Save scheduled bot
                </AppButton>
            </div>
        </div>
    );
}

export default ScheduledBotForm;